import { memo, useCallback, useEffect, useState } from "react";
import { Link as RouterLink } from "@remix-run/react";
import { Image, Button, Chip, Tooltip } from "@heroui/react";
import { BsCart3 } from "react-icons/bs";
import { FiEye } from "react-icons/fi";
import { formatToTwoDecimalPlaces, isNonChromium, toKebabCase, toTitleCase } from "app/utils/fn";
import { isProductPreviewOpen, productPreviewAtom } from "app/atoms/product.atom";
import { useSetAtom, useAtomValue } from "jotai";
import { userAtom } from "app/atoms/user.atom";
import { UserType } from "app/api/user/user.types";

const getCloudinaryUrl = (imageSrc: String, width: number) => {
  // Assumes `image` is something like:
  // "https://res.cloudinary.com/promodata/promobrands/p737_group_branded.jpg"
  // and replaces "/promobrands/" with "/f_auto,q_70,w_{width}/promobrands/"
  return imageSrc.replace(
    /https:\/\/res\.cloudinary\.com\/promodata\//,
    `https://res.cloudinary.com/promodata/f_auto,q_70,w_${width}/`,
  );
};
export type ProductCardProps = {
  image: string;
  images?: string[];
  productCode?: string;
  title: string;
  labels: string[];
  description: string;
  userTypePrice?: Record<string, { min: number; max: number }>;
  qty: { min: number; max: number };
  category: string;
  subCatgory: string;
  slug: string;
  isSpecialSupplier: boolean;
};

export const ProductCard = memo(function ProductCard({
  image,
  images,
  productCode,
  title,
  description,
  userTypePrice,
  qty,
  category,
  subCatgory,
  slug,
  labels,
  isSpecialSupplier,
}: ProductCardProps) {
  const setProduct = useSetAtom(productPreviewAtom);
  const setIsProductPreviewOpen = useSetAtom(isProductPreviewOpen);
  const user = useAtomValue(userAtom);

  const [isNonChrom, setIsNonChrom] = useState<boolean | null>(false);
  const productUrl = `/category/${category ? toKebabCase(category) : "_"}/${
    subCatgory ? toKebabCase(subCatgory) : "_"
  }/${slug}`;

  const [objectFit, setObjectFit] = useState("object-contain");

  const handleImageLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const img = e.currentTarget;
    setObjectFit(img.naturalHeight > img.clientHeight ? "object-fill" : "object-contain");
  };

  // Get the appropriate price based on user type
  const getPriceForUser = (): { min: number; max: number } | null => {
    if (userTypePrice) {
      const userType = user?.userType || UserType.Non_VIP;

      // userTypePrice uses string keys matching UserType enum values
      // UserType.Non_VIP = "Non-VIP", UserType.Gold_VIP = "Gold VIP", etc.
      return userTypePrice[userType] || null;
    }

    return null;
  };

  const displayPrice = getPriceForUser();
  // Prepare props for preview.
  const previewProps = {
    image,
    images,
    productCode,
    title,
    description,
    basePrice: displayPrice,
    qty,
    category,
    subCatgory,
    slug,
  };

  const handlePreviewProd = useCallback(
    (product: any) => {
      setProduct(product);
      setIsProductPreviewOpen(true);
    },
    [setProduct],
  );

  useEffect(() => {
    setIsNonChrom(isNonChromium());
  }, []);

  return (
    <article className="cursor-pointer group w-full" aria-label={`Product: ${title}`}>
      <figure className="aspect-square w-full relative overflow-hidden rounded-lg p-0 md:mb-4 bg-white">
        {image ? (
          <RouterLink to={productUrl} prefetch="none">
            <Image
              width="100%"
              height="auto"
              alt={title}
              loading="lazy"
              removeWrapper={true}
              // Use the 900px-wide version as the default src
              src={getCloudinaryUrl(image, 900)}
              // Provide multiple image resolutions for responsive loading
              srcSet={`
    ${getCloudinaryUrl(image, 255)} 255w,
    ${getCloudinaryUrl(image, 256)} 256w,
    ${getCloudinaryUrl(image, 606)} 606w,
    ${getCloudinaryUrl(image, 882)} 882w,
    ${getCloudinaryUrl(image, 900)} 900w,
    ${getCloudinaryUrl(image, 1160)} 1160w
  `}
              // Adjust sizes based on your layout requirements.
              // Here, for example, small viewports use ~234px, medium viewports 50vw, and larger viewports 33vw.
              sizes="(min-width: 1740px) 363px, (min-width: 1280px) calc(19.55vw + 27px), (min-width: 1040px) calc(7.27vw + 144px), (min-width: 900px) 236px, (min-width: 780px) calc(18vw + 78px), (min-width: 700px) 314px, calc(47.89vw - 12px)"
              onLoad={handleImageLoad}
              {...(isNonChrom && { "data-loaded": isNonChrom })}
              classNames={{
                img: ` abc max-h-[300px] xl:max-h-72 2xl:max-h-[19rem] border-2 border-zinc-100 rounded-lg overflow-hidden w-full ${objectFit}`,
              }}
            />
          </RouterLink>
        ) : (
          <div className="bg-slate-100 h-full w-full" aria-hidden="true" />
        )}
        <div className="hidden md:grid grid-cols-2 absolute bottom-0 opacity-0 group-hover:opacity-100 transition w-full z-20 rounded-b-lg overflow-hidden">
          <Button
            radius="none"
            className="bg-orange-700 text-white"
            startContent={<FiEye />}
            onPress={() => handlePreviewProd(previewProps)}
            aria-label={`Preview ${title}`}
          >
            Preview
          </Button>
          <Button
            as={RouterLink}
            to={productUrl}
            prefetch="none"
            radius="none"
            className="bg-primary text-white"
            startContent={<BsCart3 />}
            aria-label={`View details of ${title}`}
          >
            View Product
          </Button>
        </div>
      </figure>

      <figcaption className="overflow-visible text-left py-2">
        <RouterLink
          to={productUrl}
          prefetch="none"
          className="text-primary capitalize font-semibold 2x:text-lg focus:outline-none focus:ring-2 focus:ring-offset-2"
        >
          {toTitleCase(title)}
        </RouterLink>
        <p className="text-forgground text-small mb-2 text-wrap line-clamp-2 truncate">
          {description}
        </p>
        <div className="flex flex-row flex-wrap items-center md:flex-nowrap text-sm justify-between">
          {displayPrice &&
            displayPrice?.min > 0 &&
            (displayPrice?.min !== displayPrice?.max || qty.min > 1 || isSpecialSupplier) && (
              <Tooltip
                showArrow
                classNames={{
                  base: ["before:bg-neutral-400 dark:before:bg-white"],
                  content: [
                    "py-2 px-4 shadow-xl",
                    "text-black bg-gradient-to-br from-white to-neutral-400",
                  ],
                }}
                content="Price excludes branding and freight"
                placement="bottom"
              >
                <div className="dark:bg-gray-900 bg-white shadow-sm border-gray-50 dark:border-none px-1 py-0.5 md:px-3 md:py-1 rounded-lg text-gray-700 dark:text-gray-200 flex flex-row gap-1">
                  <span className="text-xs font-medium">
                    from{" "}
                    <span className="text-orange-700 dark:text-orange-300 text-small">
                      ${formatToTwoDecimalPlaces(displayPrice.min)}
                    </span>{" "}
                    to
                    <span className="text-primary text-small">
                      {" "}
                      ${formatToTwoDecimalPlaces(displayPrice.max)}
                    </span>
                  </span>
                </div>
              </Tooltip>
            )}
          {qty && qty.min > 0 && <div className="text-xs">{qty.min} min qty</div>}
        </div>

        {labels && labels.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-2">
            {labels.map((label) => (
              <Chip
                key={label}
                size="sm"
                className={`${
                  label === "New Release"
                    ? "bg-green-700"
                    : label === "On Sale"
                    ? "bg-primary"
                    : label === "Best Buy"
                    ? "bg-orange-700"
                    : "bg-purple-600"
                } text-white`}
              >
                {label}
              </Chip>
            ))}
          </div>
        )}
      </figcaption>
    </article>
  );
});
